/**
 * "Update available — Reload": shown only once a newer build has shipped.
 *
 * Sits beside the status line (the rail's footer, the phone's top row) because
 * it is the same kind of quiet, shell-level fact: nothing pops, nothing is
 * pushed, and it is absent the rest of the time. A hidden tab reloads itself
 * (`appUpdateChecker`), so the chip is mostly seen by a tab kept in front.
 * Clicking reloads — after any unsaved edit lands (`requestReload`).
 */
import React, { useSyncExternalStore } from 'react';

import { getSnapshot, requestReload, subscribe } from '@shared/app-update/appUpdateStore';
import { Icons } from '@shared/components/ui';

interface UpdateChipProps {
  /** Icon-only: the collapsed rail and the phone's top row. */
  compact?: boolean;
}

const UpdateChip: React.FC<UpdateChipProps> = ({ compact = false }) => {
  const { updateAvailable, reloadPending } = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getSnapshot
  );
  if (!updateAvailable) return null;

  const label = reloadPending ? 'Saving, then reloading…' : 'Update available — Reload';
  if (compact) {
    return (
      <button
        type="button"
        aria-label={label}
        title={label}
        disabled={reloadPending}
        onClick={requestReload}
        className="hover:bg-sidebar-accent text-sidebar-foreground flex h-8 w-8 shrink-0 items-center justify-center rounded-sm"
      >
        <Icons.Refresh size="xs" color="inherit" />
      </button>
    );
  }
  return (
    <div className="px-3 py-1">
      <button
        type="button"
        disabled={reloadPending}
        onClick={requestReload}
        className="hover:bg-sidebar-accent text-sidebar-foreground -mx-1 flex w-[calc(100%+0.5rem)] items-center gap-2 rounded-sm px-1 py-0.5 text-left text-xs"
      >
        <Icons.Refresh size="xs" color="inherit" />
        {reloadPending ? (
          <span className="min-w-0 truncate">Saving, then reloading…</span>
        ) : (
          <span className="min-w-0 truncate">
            Update available — <span className="font-medium">Reload</span>
          </span>
        )}
      </button>
    </div>
  );
};

export default UpdateChip;
